import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import { ContentProvider } from './context/ContentContext';
import { Toaster } from './components/ui/sonner';
import Home from './pages/Home';
import AuthCallback from './components/AuthCallback';
import ThemePicker from './components/ThemePicker';
import CartDrawer from './components/CartDrawer';
import ScrollManager from './components/ScrollManager';
import CookieConsent from './components/CookieConsent';
import { useSiteContent } from './context/ContentContext';
import { useEffect } from 'react';
import { THEMES, applyThemeVars, resetThemeVars, resolveTheme, applyMode, scaleTriplet, LIGHT_ACCENT_FACTOR } from './lib/theme';

const Admin = lazy(() => import('./pages/Admin'));
const Account = lazy(() => import('./pages/Account'));
const Blog = lazy(() => import('./pages/Blog'));
const Article = lazy(() => import('./pages/Article'));
const Shop = lazy(() => import('./pages/Shop'));
const Product = lazy(() => import('./pages/Product'));
const Contact = lazy(() => import('./pages/Contact'));
const MentionsLegales = lazy(() => import('./pages/MentionsLegales'));
const Confidentialite = lazy(() => import('./pages/Confidentialite'));
const NotFound = lazy(() => import('./pages/NotFound'));
const PaymentResult = lazy(() => import('./pages/PaymentResult'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));

const PageFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-void">
    <span className="h-8 w-8 animate-spin rounded-full border-2 border-white/10 border-t-neon" />
  </div>
);

const ThemeController = () => {
  const { data } = useSiteContent();
  useEffect(() => {
    const apply = () => {
      const mode = localStorage.getItem('tf_mode') || data?.theme?.mode || 'dark';
      applyMode(mode);
      const scale = (t) => (mode === 'light'
        ? { neon: scaleTriplet(t.neon, LIGHT_ACCENT_FACTOR), cyber: scaleTriplet(t.cyber, LIGHT_ACCENT_FACTOR), violet: scaleTriplet(t.violet, LIGHT_ACCENT_FACTOR) }
        : t);
      const local = localStorage.getItem('tf_theme');
      if (local) {
        const t = THEMES.find((x) => x.id === local);
        if (t) { applyThemeVars(scale(t)); return; }
      }
      resetThemeVars();
      const site = resolveTheme(data?.theme);
      if (site && (site.id !== 'emeraude' || site.neon)) applyThemeVars(scale(site));
    };
    apply();
    window.addEventListener('tf-theme', apply);
    return () => window.removeEventListener('tf-theme', apply);
  }, [data]);
  return null;
};

const AppRoutes = () => {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/boutique" element={<Shop />} />
        <Route path="/produit/:slug" element={<Product />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/compte" element={<Account />} />
        <Route path="/reinitialiser" element={<ResetPassword />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<Article />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/mentions-legales" element={<MentionsLegales />} />
        <Route path="/confidentialite" element={<Confidentialite />} />
        <Route path="/payment/success" element={<PaymentResult variant="success" />} />
        <Route path="/payment/cancel" element={<PaymentResult variant="cancel" />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <AuthProvider>
      <ContentProvider>
        <CartProvider>
        <BrowserRouter>
          <div className="grain relative min-h-screen bg-void text-slate-100">
            <ThemeController />
            <ScrollManager />
            <AppRoutes />
            <CartDrawer />
            <ThemePicker />
            <CookieConsent />
            <Toaster theme="dark" position="bottom-center" />
          </div>
        </BrowserRouter>
        </CartProvider>
      </ContentProvider>
    </AuthProvider>
  );
}

export default App;
